import { useEffect, useRef, useState } from 'react';
import { useAuthInfo } from '@propelauth/react';
import { useQueryClient } from '@tanstack/react-query';
import { config } from '../config';

interface SyncResponse {
  success: boolean;
  user_details: {
    hasseenintro: boolean;
    [key: string]: any;
  };
}

export const useAuthSync = () => {
  const { isLoggedIn, accessToken, user } = useAuthInfo();
  const queryClient = useQueryClient();
  const isSyncingRef = useRef(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const syncUser = async () => {
      const userId = user?.userId;
      if (!isLoggedIn || !accessToken || !userId || isSyncingRef.current) {
        return;
      }

      // Check if we've already synced this user in this session
      const sessionKey = `userSynced_${userId}`;
      const hasSyncedThisSession = sessionStorage.getItem(sessionKey);
      
      if (hasSyncedThisSession) {
        return;
      }

      // Prevent multiple concurrent sync requests
      isSyncingRef.current = true;
      setIsSyncing(true);

      try {
        const syncResponse = await fetch(`${config.API_URL}/auth/sync-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': config.API_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        });

        if (syncResponse.ok) {
          const syncData: SyncResponse = await syncResponse.json();

          // [ENG-427] Always mark synced and invalidate when HTTP succeeds.
          // sync-user now creates the user row if missing, so a 200 response
          // guarantees the row exists and GET /api/user will succeed on refetch.
          sessionStorage.setItem(sessionKey, 'true');
          queryClient.invalidateQueries({ queryKey: ['userDetails', userId] });

          if (import.meta.env.DEV) {
            console.log('[useAuthSync] User synced, hasseenintro:', syncData.user_details?.hasseenintro);
          }
        } else {
          console.error('Failed to sync user:', syncResponse.statusText);
        }
      } catch (error) {
        console.error('Error syncing user:', error);
      } finally {
        isSyncingRef.current = false;
        setIsSyncing(false);
      }
    };

    // Only attempt sync if all required conditions are met
    const userId = user?.userId;
    if (isLoggedIn && accessToken && userId) {
      // Remember current userId so we can clear the session key on logout
      prevUserIdRef.current = userId;
      syncUser();
    }
  }, [isLoggedIn, accessToken, user?.userId, queryClient]); // Only depend on userId, not the entire user object

  // Clean up session storage when user logs out
  useEffect(() => {
    if (!isLoggedIn && prevUserIdRef.current) {
      const sessionKey = `userSynced_${prevUserIdRef.current}`;
      sessionStorage.removeItem(sessionKey);
      prevUserIdRef.current = null;
    }
  }, [isLoggedIn]);

  return { isSyncing };
};